import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Papa from "papaparse";
import { IconArrowLeft, IconUpload, IconDownload, IconCheck, IconX, IconAlertCircle } from "@tabler/icons-react";
import { uploadBulkInventoryItems } from "../controllers/inventory.controller";

const InventoryImportPage = () => {
  const navigate = useNavigate();
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileSelected, setFileSelected] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileSelected(file.name);
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setCsvData(results.data);
        },
        error: (err) => {
          toast.error("Error parsing CSV file");
          console.error(err);
        }
      });
    }
  };

  const handleDownloadTemplate = () => {
    const csv = Papa.unparse([
      {
        title: "Cooking Oil",
        category: "Category",
        quantity: "50",
        unit: "Litre",
        min_quantity_threshold: "10"
      }
    ]);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "inventory_items_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    if (csvData.length === 0) {
      toast.error("No data to import");
      return;
    }

    setLoading(true);
    try {
      await uploadBulkInventoryItems(csvData);
      toast.success("Inventory items imported successfully");
      navigate("/dashboard/inventory");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to import inventory items");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/inventory"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <IconArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Import Inventory Items</h1>
        </div>
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <IconDownload className="w-4 h-4" />
          Download Template
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8 flex flex-col items-center justify-center border-b border-gray-100">
          <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mb-4">
            <IconUpload className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Upload CSV File</h3>
          <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
            Upload your inventory items CSV file. Make sure to follow the template format for successful import.
          </p>
          
          <label className="relative cursor-pointer">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2">
              {fileSelected || "Select CSV File"}
            </span>
          </label>
        </div>

        {csvData.length > 0 && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">
                Preview ({csvData.length} items)
              </h4>
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? "Importing..." : (
                  <>
                    <IconCheck className="w-4 h-4" />
                    Confirm Import
                  </>
                )}
              </button>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">Title</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Quantity</th>
                    <th className="px-4 py-3">Unit</th>
                    <th className="px-4 py-3">Threshold</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {csvData.slice(0, 10).map((row, index) => (
                    <tr key={index} className="bg-white">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.title}</td>
                      <td className="px-4 py-3">{row.category || "Category"}</td>
                      <td className="px-4 py-3">{row.quantity}</td>
                      <td className="px-4 py-3">{row.unit}</td>
                      <td className="px-4 py-3">{row.min_quantity_threshold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvData.length > 10 && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Showing first 10 items...
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 text-amber-800">
        <IconAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold mb-1">Important Instructions:</p>
          <ul className="list-disc list-inside space-y-1 text-amber-700/90">
            <li>Ensure the column headers match the template exactly.</li>
            <li>Category is optional (defaults to 'Category' if left blank).</li>
            <li>Quantity and Threshold should be numeric values.</li>
            <li>Unit can be any string (e.g., kg, pcs, litre).</li>
            <li>Initial stock movement logs will be created automatically.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InventoryImportPage;
